// menu_display.js
import { useState, useEffect, useCallback, useRef } from "react";
import { makeRequest } from "../../../../assets/functions_helper";
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  Container,
  FormControl,
  Select,
  MenuItem,
  AppBar,
  Button,
} from "@mui/material";
import { useParams } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ProductCardDisplay, OrderProductCardDisplay } from "./cards";
import { WorldCupRenderer } from "./world_cup_templates/WorldCupRenderer";
import { worldCupThemeAssets } from "./world_cup_templates/themeAssets";

// Header rediseñado para que coincida con el estilo de MenuClients
const MenuHeader = ({ menuSections, onScrollToSection, menuConfig }) => {
  const [selectedSection, setSelectedSection] = useState("Todo el menú");

  const handleChange = (event) => {
    const value = event.target.value;
    setSelectedSection(value);
    onScrollToSection(value);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        top: 0,
        zIndex: 1000,
        backgroundColor: menuConfig.header?.background_color || "#ffffff",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        py: 2,
        borderBottom: menuConfig.darkMode
          ? "1px solid rgba(255,255,255,0.05)"
          : "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {/* Logo centrado */}
          <Box sx={{ mb: 2 }}>
            {menuConfig.logoUrl ? (
              <Box
                component="img"
                src={menuConfig.logoUrl}
                alt="Logo del restaurante"
                sx={{
                  width: { xs: 70, sm: 90 },
                  height: { xs: 70, sm: 90 },
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: menuConfig.darkMode
                    ? "2px solid rgba(255,255,255,0.1)"
                    : "2px solid rgba(0,0,0,0.05)",
                }}
              />
            ) : (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  color: menuConfig.darkMode ? "#FFFFFF" : "#1A1A1A",
                  textAlign: "center",
                }}
              >
                <span style={{ color: menuConfig.globalButtons || "#C5A059" }}>
                  El Chuzo
                </span>{" "}
                de JUAN
              </Typography>
            )}
          </Box>

          {/* Selector de categorías estilo botón */}
          <FormControl fullWidth size="small" variant="outlined">
            <Select
              value={selectedSection}
              onChange={handleChange}
              displayEmpty
              IconComponent={ExpandMoreIcon}
              sx={{
                backgroundColor:
                  menuConfig.filter?.background_color ||
                  (menuConfig.darkMode ? "#2A2A2A" : "#F5F5F5"),
                color:
                  menuConfig.filter?.text_color ||
                  (menuConfig.darkMode ? "#FFFFFF" : "#1A1A1A"),
                borderRadius: "16px",
                textTransform: "none",
                fontWeight: 600,
                py: 1.2,
                px: 2.5,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: menuConfig.darkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.1)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: menuConfig.darkMode
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.2)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: menuConfig.globalButtons || "#C5A059",
                },
                "& .MuiSvgIcon-root": {
                  color:
                    menuConfig.filter?.text_color ||
                    (menuConfig.darkMode ? "#FFFFFF" : "#1A1A1A"),
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "16px",
                    mt: 1,
                    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                    "& .MuiMenuItem-root": {
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-selected": {
                        backgroundColor:
                          menuConfig.filter?.background_color || "#f0f0f0",
                        color: menuConfig.filter?.text_color || "#000",
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="Todo el menú">Todo el menú</MenuItem>
              {menuSections.map((s) => (
                <MenuItem key={s.name} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Container>
    </AppBar>
  );
};

export default function MenuDisplay() {
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState([]);
  const [menuConfig, setMenuConfig] = useState({ darkMode: false });
  const [filteredSections, setFilteredSections] = useState([]);
  const [messageError, setMessageError] = useState("");

  const sectionRefs = useRef({});
  const { tableNumber } = useParams();

  const scrollToSection = (sectionName) => {
    if (sectionName === "Todo el menú") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const sectionElement = sectionRefs.current[sectionName];
    if (sectionElement) {
      const offset = 160;
      const elementPosition = sectionElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  const fetchMenu = useCallback(async () => {
    setLoading(true);
    try {
      const response = await makeRequest(
        "POST",
        `menu_manager/public_menu?restaurant_id=${tableNumber || "3test"}`,
        {},
        "application/json"
      );

      const sections = response.menu.sections || [];
      sections.sort((a, b) => (a.order || 0) - (b.order || 0));

      setMenu(response);
      setFilteredSections(sections);

      if (response.menu) {
        const bgColor = response.menu.background_color;
        const darkMode = bgColor
          ? (() => {
              const c = bgColor.substring(1);
              const rgb = parseInt(c, 16);
              const r = (rgb >> 16) & 0xff;
              const g = (rgb >> 8) & 0xff;
              const b = (rgb >> 0) & 0xff;
              return 0.2126 * r + 0.7152 * g + 0.0722 * b < 128;
            })()
          : false;

        setMenuConfig({
          header: response.menu.header_config || {},
          filter: response.menu.filter_config || {},
          globalBackground: response.menu.background_color || "#f9fafb",
          globalButtons: response.menu.buttons_color || "#C5A059",
          productBackground: response.menu.product_background_color,
          productText: response.menu.product_text_color,
          logoUrl: response.menu.logo_url,
          backgroundImageUrl: response.menu.background_image_url, // <-- NUEVO: guardamos la URL de la imagen de fondo
          themeId: response.menu.theme_id || null,
          darkMode,
        });
      }

      setMessageError("");
    } catch (err) {
      console.error("Error fetching menu:", err);
      setMessageError("No pudimos cargar el menú en este momento.");
    } finally {
      setLoading(false);
    }
  }, [tableNumber]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  // Ajuste dinámico de fondo del body para evitar barras blancas en móviles y overscroll
  useEffect(() => {
    if (menuConfig.themeId) {
      const originalBg = document.body.style.backgroundColor;
      document.body.style.backgroundColor = '#00051e';
      return () => {
        document.body.style.backgroundColor = originalBg;
      };
    }
  }, [menuConfig.themeId]);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress sx={{ color: menuConfig.globalButtons || "#C5A059" }} />
        </Box>
      ) : messageError && filteredSections.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            px: 4,
            textAlign: "center",
            backgroundColor: "#f9fafb",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#333" }}>
            {messageError}
          </Typography>
          <Button
            variant="contained"
            onClick={fetchMenu}
            sx={{
              backgroundColor: menuConfig.globalButtons || "#C5A059",
              color: "#ffffff",
              borderRadius: "12px",
              px: 4,
              py: 1.5,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { opacity: 0.9 },
            }}
          >
            Reintentar cargar menú
          </Button>
        </Box>
      ) : (
        // Contenedor principal con imagen de fondo (si existe)
        <Box sx={{
          position: 'relative',
          minHeight: '100vh',
          backgroundColor: (menuConfig.themeId && worldCupThemeAssets[menuConfig.themeId]) ? '#00051e' : 'transparent'
        }}>
          {menuConfig.themeId && worldCupThemeAssets[menuConfig.themeId] ? (
            <WorldCupRenderer
              sections={filteredSections}
              theme={worldCupThemeAssets[menuConfig.themeId]}
              logoUrl={menuConfig.logoUrl}
            />
          ) : (
            <>
              {/* Imagen de fondo con blur (si existe) */}
              {menuConfig.backgroundImageUrl && (
            <Box sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${menuConfig.backgroundImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: 'scale(1.02)',
              zIndex: -1,
            }} />
          )}

          {/* Contenido principal con overlay semitransparente si hay imagen */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              backgroundColor: menuConfig.backgroundImageUrl
                ? 'rgba(0,0,0,0.2)'
                : menuConfig.globalBackground,
              minHeight: '100vh',
            }}
          >
            <MenuHeader
              menuSections={menu.menu?.sections || []}
              onScrollToSection={scrollToSection}
              menuConfig={menuConfig}
            />

            <Container maxWidth="md" sx={{ mt: 3, pb: 6 }}>
              {filteredSections.map((section, idx) => (
                <Box
                  key={idx}
                  sx={{ mb: 6 }}
                  ref={(el) => (sectionRefs.current[section.name] = el)}
                >
                  {/* Cabecera de sección estilo MenuClients */}
                  <Box
                    sx={{
                      position: "relative",
                      height: "180px",
                      borderRadius: "24px",
                      overflow: "hidden",
                      mb: 4,
                      boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                      transition: "transform 0.3s",
                      "&:hover": { transform: "scale(1.01)" },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `url(${
                          section.background_image_url ||
                          "https://via.placeholder.com/800x400?text=Sección"
                        })`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        zIndex: 1,
                        filter: "brightness(0.8)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        zIndex: 2,
                      }}
                    />
                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        pl: 4,
                        pr: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 900,
                          color: "#FFFFFF",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          mb: 1,
                        }}
                      >
                        {section.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: "32px",
                            height: "3px",
                            bgcolor: menuConfig.globalButtons || "#C5A059",
                            borderRadius: "2px",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.9)",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                          }}
                        >
                          {section.products.length}{" "}
                          {section.products.length === 1 ? "Plato" : "Platos"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Grid de productos */}
                  <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
                    {section.products.map((product) => {
                      const hasImage = product.image_url;
                      return (
                        <Grid item xs={12} sm={6} key={product.product_id || product.id}>
                          <Box
                            sx={{
                              "& .MuiCard-root": {
                                backgroundColor: `${
                                  section.background_color_cards ||
                                  menuConfig.productBackground ||
                                  (menuConfig.darkMode ? "#1E1E1E" : "#FFFFFF")
                                } !important`,
                                boxShadow: `0 8px 20px rgba(0,0,0,0.12), 0 4px 12px ${
                                  section.button_color_cards ||
                                  section.styles?.buttons_color ||
                                  menuConfig.globalButtons ||
                                  "#C5A059"
                                }80 !important`,
                                color: `${
                                  section.text_color_cards ||
                                  section.styles?.product_text_color ||
                                  menuConfig.productText ||
                                  (menuConfig.darkMode ? "#FFFFFF" : "#1A1A1A")
                                } !important`,
                                borderRadius: "20px !important",
                                "&:hover": {
                                  transform: "translateY(-4px) !important",
                                  boxShadow:
                                    "0 8px 30px rgba(0,0,0,0.15) !important",
                                },
                              },
                              "& .MuiTypography-root": {
                                color: `${
                                  section.text_color_cards ||
                                  section.styles?.product_text_color ||
                                  menuConfig.productText ||
                                  (menuConfig.darkMode ? "#FFFFFF" : "#1A1A1A")
                                } !important`,
                              },
                              "& .price-text": {
                                color: `${
                                  section.button_color_cards ||
                                  section.styles?.buttons_color ||
                                  menuConfig.globalButtons ||
                                  "#C5A059"
                                } !important`,
                                fontWeight: 900,
                                fontSize: "1.2rem",
                              },
                            }}
                          >
                            {hasImage ? (
                              <ProductCardDisplay
                                product={product}
                                sectionStyles={section.styles || {}}
                                buttonColorCards={section.button_color_cards}
                                backgroundColorCards={section.background_color_cards}
                                textColorCards={section.text_color_cards}
                              />
                            ) : (
                              <OrderProductCardDisplay
                                product={product}
                                sectionStyles={section.styles || {}}
                                buttonColorCards={section.button_color_cards}
                                backgroundColorCards={section.background_color_cards}
                                textColorCards={section.text_color_cards}
                              />
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Container>
          </Box>
            </>
          )}
        </Box>
      )}
    </>
  );
}